import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Colors } from '.';
import { clamp, listen } from '../CodesThatsGottaGoSomewhereElse';
import { Point } from '../platform/geom/Point';
import { Rect } from '../platform/geom/Rect';

const Track = styled.div`
    height: 0.5rem;
    margin: .25rem 0;
    padding-right: 0.5rem;
    background: ${Colors.gray200};
    border-radius: 9999px;
    background-color: rgba(33,65,171,0.2);
    user-select: none;
`;

const TrackInner = styled.div<{ f: number }>`
    padding-left: ${p => p.f * 100}%;
`;

const Knob = styled.div`
    height: 1rem;
    width: 1rem;
    cursor: grab;
    box-sizing: border-box;
    border-radius: 9999px;
    outline: none!important;
    background-color: ${Colors.primary};
    transform: translate(-.25rem, -.25rem);
    user-select: none;

    &:active, &:focus {
        box-shadow: 0 0 0 3px rgb(118 169 250 / 45%);
    }
`;

const Frame = styled.div<{ disabled: boolean }>`
    ${p => p.disabled ? css`
    
    pointer-events: none;
    ${Knob} {
        background: #aabcf8;
    }
    
    ` : ''}
`;

export type SliderProps = {
    min?: number,
    max?: number,
    inc?: number,
    value: number,
    tabIndex?: number,
    disabled?: boolean,
    onChange: (value: number) => void,
};

export const Slider = ({ min, max, inc, value, tabIndex, disabled, onChange }: SliderProps) => {
    min = min ?? 0;
    max = max ?? 100;
    inc = inc ?? 1;

    const f = value / max;

    const frame = useRef<HTMLDivElement>();
    const [dragging, setDragging] = useState(false);

    const onSet = useCallback((next: number) => {
        next = Math.min(max, Math.max(min, clamp(next, inc)));
        if (next !== value) onChange(next);
    }, [min, max, inc, value, onChange]);

    const onDrag = useCallback((e: any) => {
        e.preventDefault();
        const tb = Rect.fromElement(frame.current);
        const xy = Point.from(e).subtract(tb.topLeft());
        onSet(max * (xy.x / tb.width));
    }, [frame.current, min, max, onSet]);

    const onStart = useCallback((e: any) => {
        setDragging(true);
        onDrag(e.nativeEvent);
    }, [onDrag]);

    const onFinish = useCallback((e: any) => {
        onDrag(e);
        setDragging(false);
    }, [onDrag]);

    useEffect(() => dragging && listen(window, 'mousemove', onDrag), [dragging, onDrag]);
    useEffect(() => dragging && listen(window, 'mouseup', onFinish), [dragging, onFinish]);

    return <Frame ref={frame} disabled={disabled} onMouseDown={onStart}>
        <Track>
            <TrackInner f={f}>
                <Knob tabIndex={tabIndex ?? -1} />
            </TrackInner>
        </Track>
    </Frame>;
};
